/* ==========================================================================
   VORE — HEADER (vore-header.js)
   --------------------------------------------------------------------------
   Tres responsabilidades, todas portadas do JS inline de raw/index.html
   (guardas globais `megaMenuDrawerPanelsAttached` e `megaMenuIndicatorAttached`):

     1. Paineis do mega menu dentro do drawer mobile ([data-mm-open]/[data-mm-close])
     2. Indicador deslizante sob o item de menu ativo/hover (.menu-listing__indicator)
     3. Medicao da altura real da barra de anuncio -> --vore-announcement-height,
        usada pelo `top` do header sticky (o site usa `top: 33px` fixo)

   Sem dependencias: o drawer em si e o <dialog-component> do tema Kind.
   ========================================================================== */

(() => {
  if (window.rstHeaderAttached) return;
  window.rstHeaderAttached = true;

  /* ---------------------------------------------------------------------
     1. Paineis do drawer mobile
     --------------------------------------------------------------------- */

  const closeAllPanels = (scope) => {
    const root = scope || document;

    root.querySelectorAll('.mm-drawer__panel.is-active').forEach((panel) => {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');

      const opener = document.querySelector(`[data-mm-open="${panel.id}"]`);
      if (opener) opener.setAttribute('aria-expanded', 'false');
    });

    root.querySelectorAll('.vore-menu-drawer__scroll').forEach((container) => {
      container.removeAttribute('aria-hidden');
    });
  };

  const openPanel = (trigger) => {
    const id = trigger.getAttribute('data-mm-open');
    const panel = id ? document.getElementById(id) : null;
    if (!panel) return;

    const drawer = panel.closest('.vore-menu-drawer') || document;
    closeAllPanels(drawer);

    panel.classList.add('is-active');
    panel.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    panel.scrollTop = 0;

    const scroller = drawer.querySelector ? drawer.querySelector('.vore-menu-drawer__scroll') : null;
    if (scroller) scroller.setAttribute('aria-hidden', 'true');

    const back = panel.querySelector('.mm-drawer__back');
    if (back) back.focus();
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-mm-open]');
    if (trigger) {
      event.preventDefault();
      openPanel(trigger);
      return;
    }

    const back = event.target.closest('[data-mm-close]');
    if (back) {
      event.preventDefault();
      const panel = back.closest('.mm-drawer__panel');
      const drawer = panel ? panel.closest('.vore-menu-drawer') : null;
      const opener = panel ? document.querySelector(`[data-mm-open="${panel.id}"]`) : null;
      closeAllPanels(drawer);
      if (opener) opener.focus();
    }
  });

  // Escape fecha primeiro o painel; so o segundo Escape fecha o drawer.
  // Fase de captura + stopPropagation impede o <dialog-component> do tema Kind
  // de fechar o drawer inteiro no primeiro toque.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;

      const open = document.querySelector('.mm-drawer__panel.is-active');
      if (!open) return;

      event.preventDefault();
      event.stopPropagation();

      const opener = document.querySelector(`[data-mm-open="${open.id}"]`);
      closeAllPanels(open.closest('.vore-menu-drawer'));
      if (opener) opener.focus();
    },
    true
  );

  // Volta ao nivel raiz sempre que o drawer for fechado.
  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      const drawer = dialog.closest('.vore-menu-drawer');
      if (drawer) closeAllPanels(drawer);
    },
    true
  );

  /* ---------------------------------------------------------------------
     2. Indicador do menu desktop
     --------------------------------------------------------------------- */

  const LINKS = '.menu-listing .menu-link';

  const getIndicator = (link) => {
    const listing = link.closest('.menu-listing');
    return listing ? listing.querySelector('.menu-listing__indicator') : null;
  };

  const moveIndicator = (link, instant) => {
    const indicator = getIndicator(link);
    if (!indicator) return;

    const listing = link.closest('.menu-listing');
    if (!listing) return;

    // O indicador e `position: absolute`, entao as coordenadas precisam ser
    // relativas ao bloco de contencao dele (o cabecalho), nao ao `.menu-listing`
    // — que de proposito nao e posicionado, para o painel do mega menu poder
    // ocupar a largura toda da tela.
    const anchor = indicator.offsetParent || listing;
    const anchorBox = anchor.getBoundingClientRect();
    const linkBox = link.getBoundingClientRect();
    const left = linkBox.left - anchorBox.left;
    const right = anchorBox.right - linkBox.right;
    const top = linkBox.bottom - anchorBox.top;
    const previousLeft = parseFloat(indicator.style.getPropertyValue('--mm-line-left')) || 0;

    indicator.classList.remove('is-moving-left', 'is-moving-right');
    indicator.classList.toggle('is-instant', Boolean(instant));

    if (!instant) {
      indicator.classList.add(left > previousLeft ? 'is-moving-right' : 'is-moving-left');
    }

    indicator.style.setProperty('--mm-line-top', `${top}px`);
    indicator.style.setProperty('--mm-line-left', `${left}px`);
    indicator.style.setProperty('--mm-line-right', `${right}px`);
    indicator.classList.add('is-visible');
  };

  const hideIndicator = (listing) => {
    const indicator = listing.querySelector('.menu-listing__indicator');
    if (!indicator) return;
    indicator.classList.remove('is-visible', 'is-moving-left', 'is-moving-right');
  };

  document.addEventListener(
    'pointerenter',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest(LINKS);
      if (link) moveIndicator(link, false);
    },
    true
  );

  document.addEventListener(
    'focusin',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest(LINKS);
      if (link) moveIndicator(link, false);
    },
    true
  );

  document.addEventListener(
    'pointerleave',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.classList.contains('menu-listing')) return;
      hideIndicator(event.target);
    },
    true
  );

  /* ---------------------------------------------------------------------
     3. Altura real da barra de anuncio
     --------------------------------------------------------------------- */

  const measureAnnouncement = () => {
    const bar = document.querySelector('.vore-announcement-section');
    const height = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--vore-announcement-height', `${height}px`);
  };

  const observeAnnouncement = () => {
    // Sempre mede: sem barra na pagina o valor precisa cair para 0px, senao o
    // header sticky fica com um vao no topo.
    measureAnnouncement();

    const bar = document.querySelector('.vore-announcement-section');
    if (!bar) return;

    if ('ResizeObserver' in window) {
      new ResizeObserver(measureAnnouncement).observe(bar);
    } else {
      window.addEventListener('resize', measureAnnouncement);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAnnouncement);
  } else {
    observeAnnouncement();
  }

  // O editor de temas recarrega sections sem recarregar a pagina.
  document.addEventListener('shopify:section:load', () => {
    measureAnnouncement();
  });
  document.addEventListener('shopify:section:unload', () => {
    measureAnnouncement();
  });
})();
